import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import api, { GenericError } from "../api"
import { PurchaseRequestModel } from "../api/purchaseRequest.api"

export interface PurchaseRequestListState {
    purchaseRequest: PurchaseRequestModel[]
    loading: boolean
    walletloading: boolean
    rejectReasons: any[]
    //TODO: make error interface
    error?: GenericError
}
export interface PurchaseRequestRejectState {
    purchase_request_id: number
    proof_of_rejection: string
    remarks: string
}

export interface CollectedStatusState {
    purchase_request_id: number
    remarks: string
}

export interface ChangeInvoiceState {
    purchase_request_id: number
    attachment: string
    is_invoice: number
}

export interface PurchaseRequestMarkPurchasedState {
    purchase_request_id: number
    proof_of_purchase: string
    transfer_balance_amount_to_wallet: number
    final_amount: number
}

const initialState: PurchaseRequestListState = {
    purchaseRequest: [],
    rejectReasons: [],
    loading: false,
    walletloading: false
}

export interface UpdateShipmentState {
    purchase_request_id: number
    shipment_number: string
}

export const loadPurchaseRequest = createAsyncThunk("requests/list", async (thunkAPI) => {
    try {
        const response = await api.purchaseRequest.purchaseRequestList()
        if (response.status) {
            return response
        }
    } catch (error: any) {
        console.log(error)
    }
})

export const loadAllRejectReasons = createAsyncThunk("requests/reject-reasons/list", async (thunkAPI) => {
    try {
        const response = await api.purchaseRequest.rejectReasonsList()
        if (response.status) {
            return response
        }
    } catch (error: any) {
        console.log(error)
    }
})

export const transferToWallet = createAsyncThunk(
    "requests/reject",
    async (rejectData: PurchaseRequestRejectState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.purchaseRequestReject(rejectData)
            if (response.status) {
                return response
            }
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const requestToDeductFromWallet = createAsyncThunk(
    "requests/deduct-from-wallet",
    async (rejectData: PurchaseRequestRejectState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.requestToDeductFromWallet(rejectData)
            return response
        } catch (error: any) {
            console.log(error)
            return thunkAPI.rejectWithValue(error)
        }
    }
)

export const changeStatusCollected = createAsyncThunk(
    "requests/collected-status",
    async (data: CollectedStatusState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.changeStatusCollected(data)
            if (response.status) {
                return response
            }
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const changeInvoice = createAsyncThunk(
    "requests/change-invoice",
    async (data: ChangeInvoiceState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.changeInvoice(data)
            if (response.status) {
                return response
            }
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const markAsPurchased = createAsyncThunk(
    "requests/mark-purchased",
    async (purchasedData: PurchaseRequestMarkPurchasedState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.markAsPurchased(purchasedData)
            if (response.status) {
                return response
            }
        } catch (error: any) {
            console.log(error)
        }
    }
)

export const updateShipmentNumber = createAsyncThunk(
    "requests/update-shipment-number",
    async (data: UpdateShipmentState, thunkAPI) => {
        try {
            const response = await api.purchaseRequest.updateShipmentNumber(data)
            if (response.status) {
                return response
            }
        } catch (error: any) {
            console.log(error)
        }
    }
)
export const purchaseRequestSlice = createSlice({
    name: "purchaseRequest",
    initialState,

    reducers: {
        clearError: (state: PurchaseRequestListState) => {
            state.error = undefined
        }
    },

    extraReducers: {
        [loadPurchaseRequest.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadPurchaseRequest.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [loadPurchaseRequest.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.purchaseRequest = action.payload?.data?.payload?.data
        },
        [loadAllRejectReasons.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadAllRejectReasons.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [loadAllRejectReasons.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.rejectReasons = action.payload?.data?.payload?.data
        },
        [transferToWallet.pending.toString()]: (state, action) => {
            state.walletloading = true
        },
        [transferToWallet.rejected.toString()]: (state, action) => {
            state.walletloading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [transferToWallet.fulfilled.toString()]: (state, action) => {
            state.walletloading = false
        },
        [changeStatusCollected.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [changeStatusCollected.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [changeStatusCollected.fulfilled.toString()]: (state, action) => {
            state.loading = false
        },
        [changeInvoice.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [changeInvoice.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [changeInvoice.fulfilled.toString()]: (state, action) => {
            state.loading = false
        },
        [requestToDeductFromWallet.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [requestToDeductFromWallet.rejected.toString()]: (state, action) => {
            state.loading = false
            if (action.payload?.response?.data?.error) {
                state.error = action.payload?.response?.data?.error
            } else {
                state.error = action.payload?.response?.data?.payload
            }
        },
        [requestToDeductFromWallet.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.message
        }
    }
})

export const { clearError } = purchaseRequestSlice.actions

export default purchaseRequestSlice.reducer
